import { useState, useCallback } from 'react';

const OLLAMA_ENDPOINT = process.env.REACT_APP_OLLAMA_ENDPOINT;
const MODEL_NAME = 'llama3.2-vision';

if (!OLLAMA_ENDPOINT) {
  console.error('❌ REACT_APP_OLLAMA_ENDPOINT is not defined in .env');
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error || new Error('File read failed'));
    reader.readAsDataURL(file);
  });
}

function extractJSON(text) {
  if (typeof text !== 'string' || !text) return null;

  try {
    return JSON.parse(text.trim());
  } catch (_) {}

  const jsonMatch = text.match(/\{[\s\S]*\}/);
  if (jsonMatch) {
    try {
      return JSON.parse(jsonMatch[0]);
    } catch (_) {}
  }
  return null;
}

function normalizeResult(parsed) {
  if (!parsed || typeof parsed !== 'object') return null;

  return {
    foodName: typeof parsed.foodName === 'string' && parsed.foodName.trim() 
      ? parsed.foodName.trim() : 'unknown',
    estimatedCalories: Number.isFinite(parsed.estimatedCalories) 
      ? parsed.estimatedCalories : 0,
    estimatedServingSize: typeof parsed.estimatedServingSize === 'string' && parsed.estimatedServingSize.trim()
      ? parsed.estimatedServingSize.trim() : 'unknown',
    confidence: ['high', 'medium', 'low'].includes(String(parsed.confidence || '').toLowerCase()) 
      ? String(parsed.confidence).toLowerCase() : 'low'
  };
}

export function useFoodPhotoScan() {
  const [scanning, setScanning] = useState(false);
  const [unavailable, setUnavailable] = useState(false);

  const scan = useCallback(async (file) => {
    if (!file) return null;
    if (!OLLAMA_ENDPOINT) {
      console.error('❌ Ollama endpoint not configured');
      setUnavailable(true);
      return null;
    }

    setScanning(true);
    setUnavailable(false);

    try {
      console.log(`📸 Starting scan using ${MODEL_NAME}`);

      const base64 = await fileToBase64(file);

      const body = {
        model: MODEL_NAME,
        options: { 
          temperature: 0.0, 
          num_predict: 80,     // Reduced for speed
          num_ctx: 2048
        },
        messages: [
          {
            role: 'user',
            content: [
              { type: 'image', data: base64, mimeType: file.type },
              { 
                type: 'text', 
                text: 'Respond with ONLY valid JSON and nothing else: {"foodName":"string","estimatedCalories":number,"estimatedServingSize":"string","confidence":"high"|"medium"|"low"}' 
              },
            ],
          },
        ],
      };

      const res = await fetch(OLLAMA_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        setUnavailable(true);
        return null;
      }

      const payload = await res.json();
      const rawContent = payload?.message?.content ?? payload?.response ?? '';
      const parsed = extractJSON(rawContent);
      const normalized = normalizeResult(parsed);

      if (!normalized || normalized.foodName === 'unknown') {
        setUnavailable(true);
        return null;
      }

      return normalized;
    } catch (err) {
      console.error('💥 Scan error:', err);
      setUnavailable(true);
      return null;
    } finally {
      setScanning(false);
    }
  }, []);

  const reset = useCallback(() => setUnavailable(false), []);

  return { scan, scanning, unavailable, reset };
}

export default useFoodPhotoScan;